import { captureException } from '@sentry/react';
import { ICheckoutItem, IPaymentMethodDiscount } from '../../interfaces/ICheckout';

export const moneyMask = {
  apply: (value: number | string | undefined | null) => {
    if (value === null || value === undefined || value === '') return '';

    const cleaned = moneyMask.clean(value);
    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(cleaned || 0);
  },

  clean: (value: number | string) => {
    if (typeof value === 'number') return value;

    value = (value || '').toString().replace(/[^\d,]/gi, '');

    /*if (!value.includes(',')) {
      value = `0,${value}`.padStart(2, '0');
    }*/

    const [, cents] = value.split(',');

    if (cents && cents.length !== 2) {
      value = value
        .replace(',', '')
        .replace(/(\d+)?(\d{2})/gi, '$1,$2')
        .replace(/^,/gi, '0,');
    }

    return parseFloat(value.replace(/\./gi, '').replace(',', '.'));
  },
};

/**
 * Desconto por forma de pagamento (em centavos), para reduzir o valor a pagar conforme o
 * método/parcelas escolhidos. Espelha o backend: base = o que é cobrado agora (produto+bump no
 * avulso; adesão+bump na recorrência com adesão), e no cartão respeita o teto `maxInstallments`.
 */
export const calculatePaymentMethodDiscount = (
  paymentMethod: string | undefined,
  installments: number | undefined,
  items: ICheckoutItem[],
  discounts: IPaymentMethodDiscount[] | undefined
): number => {
  if (!paymentMethod || !discounts?.length) return 0;

  const discount = discounts.find((d) => d.paymentMethod === paymentMethod);

  if (!discount || discount.value <= 0) return 0;

  if (
    paymentMethod === 'credit_card' &&
    discount.maxInstallments &&
    (installments ?? 1) > discount.maxInstallments
  ) {
    return 0;
  }

  const hasEnrollmentFee = items.some((item) => item.type === 'enrollment_fee');
  const validItems = items.filter(
    (item) => !hasEnrollmentFee || ['enrollment_fee', 'order_bump'].includes(item.type)
  );

  // Base do desconto = o que é cobrado agora: com adesão, entrada + bump; sem adesão, produto + bump
  // (espelha o pay-time do backend). A mensalidade nunca entra. A flag `participatesInPaymentDiscount`
  // é opcional só nos order bumps — produto principal e adesão são a base da venda e sempre participam.
  const allowed = hasEnrollmentFee
    ? ['enrollment_fee', 'order_bump']
    : ['main', 'cart', 'order_bump'];
  const participating = validItems.filter(
    (item) =>
      allowed.includes(item.type) &&
      (item.type !== 'order_bump' || item.participatesInPaymentDiscount !== false)
  );
  const itemBase = (item: ICheckoutItem) =>
    (item.couponItem?.amount ?? item.unitValue ?? item.amount) * item.quantity;

  const base = participating.reduce((acc, item) => acc + itemBase(item), 0);
  if (base <= 0) return 0;

  // Fixo: valor absoluto, limitado ao total participante. Percentual: % de cada item participante.
  if (discount.mode === 'fixed') {
    return Math.min(discount.value, base);
  }

  return participating.reduce(
    (acc, item) => acc + Math.round(itemBase(item) * (discount.value / 100.0 / 100.0)),
    0
  );
};

/**
 * Total a exibir (em reais) = soma dos valores AUTORIZADOS na transação (a base pós-desconto de
 * cada pagamento), que é exatamente o que o backend valida (`soma(payment.value) == calcTotal`).
 * O parcelamento com juros é custo do cliente junto à emissora e aparece só no detalhe
 * ("Nx de R$ Y"), não na manchete — somar `installmentValue × nº` aqui inflava o total (não bate
 * com o autorizado) e escorregava centavos por arredondamento de parcela (ex.: 7x de 171,43 =
 * 1.200,01). `fallback` (reais) cobre o instante inicial antes de `payments` existir.
 */
export const getPaymentTotal = (payments: any[] | undefined, fallback: number): number => {
  const list = payments ?? [];
  if (!list.length) return fallback;

  const sum = list.reduce((acc, payment) => acc + (moneyMask.clean(payment?.value ?? 0) || 0), 0);

  return sum || fallback;
};

/**
 * Rótulo curto do pagamento ("Cartão · 2x de R$ 54,00", "Cartão à vista", "PIX", "Boleto") ou,
 * com mais de um pagamento, "N formas de pagamento".
 */
export const getPaymentDetailLabel = (payments: any[] | undefined): string => {
  const list = payments ?? [];
  if (list.length === 0) return '';
  if (list.length > 1) return `${list.length} formas de pagamento`;

  const [payment] = list;
  const method = payment?.paymentMethod;
  const installments = payment?.creditCard?.installments;
  const installmentValue = payment?.creditCard?.installmentValue;

  if (method === 'credit_card') {
    return installments > 1 && installmentValue > 0
      ? `Cartão · ${installments}x de R$ ${moneyMask.apply(installmentValue)}`
      : 'Cartão à vista';
  }
  if (method === 'pix') return 'PIX';
  if (method === 'boleto') return 'Boleto';
  return '';
};

export type PaymentSummaryView = {
  labelText: string;
  amountText: string;
  detailText: string;
  ctaText: string;
  // Total COM juros (parcela × nº), só quando o cliente escolhe parcelas com juros. Exibido numa
  // linha discreta ("Total: R$ X") junto do detalhe — nesse caso o botão fica sem valor.
  interestTotalText?: string;
};

// Total com juros (em reais) quando o pagamento único é cartão parcelado ACIMA do teto sem juros
// (`freeInstallments`) — é o que o cliente de fato paga (parcela × nº). Null nos demais casos.
const cardInterestTotal = (payments: any[] | undefined, freeInstallments: number): number | null => {
  const list = payments ?? [];
  if (list.length !== 1) return null;

  const payment = list[0];
  const installments = payment?.creditCard?.installments;
  const installmentValue = payment?.creditCard?.installmentValue;
  if (
    payment?.paymentMethod === 'credit_card' &&
    installments > (freeInstallments || 1) &&
    installmentValue > 0
  ) {
    return installmentValue * installments;
  }
  return null;
};

// Sufixo de período da recorrência: "/mês" (intervalo 1) ou " a cada 2 meses" (intervalo > 1).
const recurrenceSuffix = (firstItem: any): string => {
  const interval = firstItem?.recurrenceInterval ?? 1;
  const freq = getFrequency(firstItem?.recurrenceFrequency, interval);
  return interval > 1 ? ` a cada ${interval} ${freq}` : `/${freq}`;
};

// "2x de R$ 50,00" quando o pagamento único é cartão parcelado; vazio à vista/multi.
const getInstallmentsLabel = (payments: any[] | undefined): string => {
  const list = payments ?? [];
  if (list.length !== 1) return '';

  const payment = list[0];
  const installments = payment?.creditCard?.installments;
  const installmentValue = payment?.creditCard?.installmentValue;
  if (payment?.paymentMethod === 'credit_card' && installments > 1 && installmentValue > 0) {
    return `${installments}x de R$ ${moneyMask.apply(installmentValue)}`;
  }
  return '';
};

/**
 * O que exibir no resumo/CTA conforme o cenário — resolve num só lugar, consumido pela Fase 1
 * (PaymentSummary) e pela barra:
 * - avulso: "Total a pagar" + valor + método + "Comprar agora · R$ X";
 * - recorrência sem adesão: "Assinatura" + "R$ X/mês" + método + "Assinar agora";
 * - recorrência com adesão: "A pagar agora" + valor da adesão + "depois R$ Y/mês" + "Assinar agora".
 * O valor NUNCA é cravado num contexto ambíguo — em assinatura o botão não leva o total avulso.
 */
export const buildPaymentSummaryView = (opts: {
  payments: any[] | undefined;
  isRecurrence: boolean;
  firstItem: any;
  items: ICheckoutItem[];
  fallbackTotal: number;
  freeInstallments?: number;
}): PaymentSummaryView => {
  const { payments, isRecurrence, firstItem, items, fallbackTotal, freeInstallments } = opts;

  if (isRecurrence) {
    const suffix = recurrenceSuffix(firstItem);
    const recurring =
      ((firstItem?.couponItem?.amount ?? firstItem?.amount ?? 0) * (firstItem?.quantity ?? 1)) / 100.0;
    const hasEnrollment = items.some((item) => item.type === 'enrollment_fee');

    if (hasEnrollment) {
      const enrollment = items.find((item) => item.type === 'enrollment_fee');
      const now = getPaymentTotal(payments, fallbackTotal);
      const installments = getInstallmentsLabel(payments);
      return {
        // Nome da adesão (igual ao header) + as parcelas escolhidas em destaque; o valor recorrente
        // vira a linha secundária ("depois R$ X/mês").
        labelText: enrollment?.description || 'Adesão',
        amountText: installments || `R$ ${moneyMask.apply(now)}`,
        detailText: `depois R$ ${moneyMask.apply(recurring)}${suffix}`,
        ctaText: 'Assinar agora',
      };
    }

    // Sem adesão, mas com order bump (cobrança única junto): o cliente paga recorrente + bump agora.
    // Mostra o valor imediato em destaque e o recorrente como linha secundária, pra não esconder
    // o que está sendo cobrado neste momento.
    const now = getPaymentTotal(payments, fallbackTotal);
    if (now > recurring + 0.001) {
      return {
        labelText: 'A pagar agora',
        amountText: `R$ ${moneyMask.apply(now)}`,
        detailText: `depois R$ ${moneyMask.apply(recurring)}${suffix}`,
        ctaText: 'Assinar agora',
      };
    }

    return {
      labelText: 'Assinatura',
      amountText: `R$ ${moneyMask.apply(recurring)}${suffix}`,
      detailText: getPaymentDetailLabel(payments),
      ctaText: 'Assinar agora',
    };
  }

  // Total autorizado (base pós-desconto) — o que vai no botão quando NÃO há juros.
  const base = getPaymentTotal(payments, fallbackTotal);

  // Com juros (parcela × nº > base): o cliente paga esse total. Pra não pôr um número no botão que
  // contradiz "12x de R$ Y", tiramos o valor do botão e mostramos o total com juros numa linha
  // discreta junto do detalhe.
  const interestTotal = cardInterestTotal(payments, freeInstallments ?? 1);
  if (interestTotal != null) {
    return {
      labelText: 'Total a pagar',
      amountText: `R$ ${moneyMask.apply(interestTotal)}`,
      detailText: getPaymentDetailLabel(payments),
      ctaText: 'Comprar agora',
      interestTotalText: `Total: R$ ${moneyMask.apply(interestTotal)}`,
    };
  }

  return {
    labelText: 'Total a pagar',
    amountText: `R$ ${moneyMask.apply(base)}`,
    detailText: getPaymentDetailLabel(payments),
    ctaText: `Comprar agora · R$ ${moneyMask.apply(base)}`,
  };
};

export const dateMask = {
  apply: (value: string) => {
    if (!value) return '';

    if (value.length === 7) {
      const [month, year] = value.split('/');
      if (year.length === 4) {
        value = `${month}/${year.slice(2)}`;
      }
    }

    // format to MM/YY
    const result = value
      .replace(/\D/g, '')
      .slice(0, 4)
      .replace(/(\d{2})\//gi, '$1')
      .replace(/(\d{2})(\d{1})/gi, '$1/$2')
      .replace(/(\d{2})(\d{2})/gi, '$1/$2')
      .slice(0, 5);

    return result;
  },

  clean: (value: string) => {
    if (!value) return value;
    return value.replace(/\D/g, '').slice(0, 4);
  },
};

export const creditCardMask = {
  apply: (value: string) => {
    if (!value) return '';
    value = value.replace(/\D/g, '');

    return value
      .slice(0, 16)
      .replace(/(\d{4})/gi, '$1 ')
      .trim();
  },

  clean: (value: string) => {
    if (!value) return value;
    return value.replace(/\D/g, '').slice(0, 16);
  },
};

export const cepMask = {
  apply: (value: string) => {
    if (!value) return '';
    value = value.replace(/\D/g, '');

    return value
      .slice(0, 8)
      .replace(/(\d{5})(\d{3})/gi, '$1-$2')
      .trim();
  },

  clean: (value: string) => {
    if (!value) return value;
    return value.replace(/\D/g, '').slice(0, 8);
  },
};

export const phoneMask = {
  apply: (value: string) => {
    if (!value) return value;
    value = value.replace(/\D/g, '');

    if (value.length < 8) {
      return value.replace(/(\d{2})(\d{1,4})/gi, '($1) $2');
    }

    return value
      .slice(0, 11)
      .replace(/(\d{2})(\d{5})(\d{1,4})/gi, '($1) $2-$3')
      .trim();
  },
  clean: (value: string) => {
    if (!value) return value;

    return value.replace(/\D/gi, '').substring(0, 20);
  },
};

export const documentMask = {
  apply: (value: string) => {
    if (!value) return '';
    value = value.replace(/\D/g, '');

    if (value.length > 11) {
      return value
        .slice(0, 14)
        .replace(/(\d{2})(\d{3})(\d{3})(\d)/, '$1.$2.$3/$4')
        .replace(/(\d{2}).(\d{3}).(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5')
        .replace(/(\d{2}).(\d{3}).(\d{3})\/(\d{4})-(\d{2})/gi, '$1.$2.$3/$4-$5')
        .trim();
    }

    return value
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3}).(\d{3})(\d)/, '$1.$2.$3')
      .replace(/(\d{3}).(\d{3}).(\d{3})/, '$1.$2.$3')
      .replace(/(\d{3}).(\d{3}).(\d{3})(\d)/, '$1.$2.$3-$4')
      .replace(/(\d{3}).(\d{3}).(\d{3}).(\d{2})/gi, '$1.$2.$3-$4')
      .trim();
  },

  clean: (value: string) => {
    if (!value) return value;
    return value.replace(/\D/g, '').slice(0, 14);
  },
};

export const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const buildPayments = async (teamId: string, data: any) => {
  const payments: any[] = [];
  let index = 0;

  for (const pay of data.payments) {
    try {
      if (pay.paymentMethod === 'pix' || pay.paymentMethod === 'boleto') {
        payments.push({
          paymentMethod: pay.paymentMethod,
          value: Math.round(moneyMask.clean(pay.value) * 100),
        });

        continue;
      }

      if (pay.creditCard?.id) {
        payments.push({
          paymentMethod: pay.paymentMethod,
          value: Math.round(moneyMask.clean(pay.value) * 100),
          creditCard: {
            id: pay.creditCard.id,
            installments: pay.creditCard.installments ?? 1,
            installmentValue: pay.creditCard.installmentValue,
            installmentHash: pay.creditCard.installmentHash,
          },
        });

        continue;
      }

      await delay(index++ * 2000);

      const expiration = pay.creditCard.expiration.split('/');

      const token = await window.Routerfy.tokenizer.generateToken({
        key: `team_${teamId}`,
        card: {
          number: creditCardMask.clean(pay.creditCard.number),
          holder: pay.creditCard.holder,
          document: documentMask.clean(data.customer.document),
          cvv: pay.creditCard.cvv,
          expiration: `${expiration[0].padStart(2, '0')}/20${expiration[1].padStart(2, '0')}`,
        },
      });

      payments.push({
        paymentMethod: pay.paymentMethod,
        value: Math.round(moneyMask.clean(pay.value) * 100),
        creditCard: {
          token,
          installments: pay.creditCard.installments ?? 1,
          installmentValue: pay.creditCard.installmentValue,
          installmentHash: pay.creditCard.installmentHash,
        },
      });
    } catch (error) {
      captureException(error, { extra: { data } });
      console.error(error);
    }
  }

  return payments;
};

export const buildCustomer = (data: any, isForeigner: boolean) => {
  if (data.customer.id) {
    return {
      id: data.customer.id,
    };
  }

  const { ddi, ...rest } = data.customer;
  const phoneNumber = phoneMask.clean(data.customer.phone);
  const ddiNumber = ddi.replace(/\D/g, '');
  const phone = `${ddiNumber}${phoneNumber}`;

  if (isForeigner) {
    return {
      ...rest,
      phone,
      type: 'foreign',
    };
  }

  const document = documentMask.clean(data.customer.document);
  const type = document.length > 11 ? 'company' : 'individual';

  const address = data.customer.address
    ? {
        ...data.customer.address,
        postalCode: cepMask.clean(data.customer.address.postalCode),
        country: 'BR',
      }
    : {};

  return {
    ...rest,
    ...(address ? { address } : {}),
    document,
    phone,
    type,
  };
};
export const buildFinancialResponsible = (data: any, isForeigner: boolean) => {
  if (!data.financialResponsible) {
    return;
  }

  const { customer, financialResponsible } = data;

  const { ddi, ...rest } = financialResponsible;
  const phoneNumber = phoneMask.clean(data.financialResponsible.phone);
  const ddiNumber = ddi.replace(/\D/g, '');
  const phone = `${ddiNumber}${phoneNumber}`;

  const hasAddress = !!(financialResponsible?.address?.street ?? customer?.address?.street);

  const address = hasAddress
    ? {
        address: financialResponsible.address?.street ?? customer.address?.street,
        addressNumber: financialResponsible.address?.number ?? customer.address?.number,
        addressComplement: financialResponsible.address?.complement ?? customer.address?.complement,
        addressNeighborhood: financialResponsible.address?.neighborhood ?? customer.address?.neighborhood,
        addressCity: financialResponsible.address?.city ?? customer.address?.city,
        addressState: financialResponsible.address?.state ?? customer.address?.state,
        addressPostalCode: cepMask.clean(financialResponsible.address?.postalCode ?? customer.address?.postalCode),
        addressCountry: isForeigner ? (financialResponsible.address?.country ?? customer.address?.country ?? 'BR') : 'BR',
      }
    : {};

  const document = isForeigner ? financialResponsible.document : documentMask.clean(financialResponsible.document);

  return {
    ...rest,
    ...(address ? { ...address } : {}),
    document,
    phone,
    personType: isForeigner ? 'foreign' : document.length > 11 ? 'company' : 'individual',
  };
};

export const maskString = (value: string, startCount = 4, endCount = 4) => {
  const start = value.slice(0, startCount);
  const end = value.slice(-endCount);

  return `${start}${'*'.repeat(value.length - startCount - endCount)}${end}`;
};

export const buildOCBData = (data: any) => {
  const payments = data.payments
    .map((payment: any) => {
      if (payment.paymentMethod !== 'credit_card') return;

      return payment.response?.card;
    })
    .filter((card: any) => !!card);

  const customer = {
    id: data.customer.id,
    name: data.customer.name,
    email: maskString(data.customer.email),
    document: maskString(data.customer.document),
    phone: maskString(data.customer.phone),
  };

  return btoa(JSON.stringify({ customer, payments }));
};

export const checkLuhn = (cardNumber: string) => {
  cardNumber = cardNumber.replace(/\D/g, '');
  let sum = 0;
  let shouldDouble = false;

  for (let i = cardNumber.length - 1; i >= 0; i--) {
    let digit = parseInt(cardNumber.charAt(i));

    if (shouldDouble) {
      digit *= 2;
      if (digit > 9) {
        digit -= 9;
      }
    }

    sum += digit;
    shouldDouble = !shouldDouble;
  }

  return sum % 10 === 0;
};

const frenquencies = {
  day: 'dia',
  week: 'semana',
  month: 'mês',
  year: 'ano',
} as {
  [key: string]: string;
};

const frenquenciesPlural = {
  day: 'dias',
  week: 'semanas',
  month: 'meses',
  year: 'anos',
} as {
  [key: string]: string;
};

export const getFrequency = (frequency: string, interval: number) => {
  return interval > 1 ? frenquenciesPlural[frequency] : frenquencies[frequency];
};
